import { useNetworkHistory, useNetworkOverview } from '../../../features/locations/use-network-overview'
import { useIdOrCode } from '../../use-id-or-code'
import { Bar, btnCls, Crumbs, mono, Note, Panel, SkelPanels, SLink, Stat, StatusText } from './ui'

const th =
  'h-7 border-b border-[var(--color-border)] px-3 text-left text-[11px] font-medium uppercase tracking-[0.06em] text-[var(--color-muted)]'
const td = 'h-7 truncate border-b border-[var(--color-border)] px-3 text-[12px]'

function Rows({ label, rec }: { label: string; rec: Record<string, string> }) {
  return (
    <>
      {Object.entries(rec).map(([k, v]) => (
        <tr key={`${label}-${k}`}>
          <td className={td} style={mono}>
            {k}
          </td>
          <td className={`${td} text-[var(--color-muted)]`}>{label}</td>
          <td className={td}>
            <StatusText status={v} />
          </td>
        </tr>
      ))}
    </>
  )
}

export function SignalNetwork() {
  const idOrCode = useIdOrCode()
  const { data, isLoading, error } = useNetworkOverview(idOrCode)
  const history = useNetworkHistory(idOrCode)
  const wanHref = `/locations/${encodeURIComponent(idOrCode)}/network/wan-settings`
  const wans = data?.status.wanInterfaces ?? []
  const peers = data?.tunnel?.peers

  return (
    <div className="flex h-full flex-col">
      <Bar
        crumbs={<Crumbs idOrCode={idOrCode} trail="network" />}
        title="Network"
        meta={
          data?.status.locationStatus && (
            <span className="text-[12px]">
              <StatusText status={data.status.locationStatus} />
            </span>
          )
        }
        right={
          <SLink to={wanHref} className={btnCls}>
            WAN settings
          </SLink>
        }
      />
      <div className="min-h-0 flex-1 overflow-auto p-4">
        {isLoading ? (
          <SkelPanels n={2} />
        ) : error || !data ? (
          <Note error>Failed to load network status{error ? `: ${error.message}` : '.'}</Note>
        ) : (
          <div className="grid gap-3">
            <Panel
              title="Summary"
              aside={
                <span className="text-[var(--color-muted)]" style={mono}>
                  polled {data.status.lastPolledAt ?? 'n/a'}
                </span>
              }
            >
              <div className="grid grid-cols-2 md:grid-cols-4">
                <Stat label="Devices" value={data.summary.total} />
                <Stat label="Up" value={data.summary.up} tone="ok" />
                <Stat label="Down" value={data.summary.down} tone={data.summary.down > 0 ? 'bad' : undefined} />
                <Stat label="Other" value={data.summary.other} />
              </div>
            </Panel>

            <Panel title="Devices & tunnels">
              {data.summary.total === 0 && !data.status.tunnels ? (
                <Note>No devices reported.</Note>
              ) : (
                <table className="w-full table-fixed border-collapse" aria-label="Devices and tunnels">
                  <thead>
                    <tr>
                      <th scope="col" className={th}>
                        Name
                      </th>
                      <th scope="col" className={`${th} w-32`}>
                        Kind
                      </th>
                      <th scope="col" className={`${th} w-32`}>
                        Status
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <Rows label="device" rec={data.status.data} />
                    <Rows label="cloudflare" rec={data.status.tunnels?.cloudflare ?? {}} />
                    <Rows label="meraki s2s" rec={data.status.tunnels?.meraki_s2s ?? {}} />
                    <Rows label="backbone" rec={data.status.backbone ?? {}} />
                  </tbody>
                </table>
              )}
            </Panel>

            <div className="grid gap-3 lg:grid-cols-2">
              <Panel title="WAN interfaces">
                {wans.length === 0 ? (
                  <Note>No WAN interfaces reported.</Note>
                ) : (
                  <table className="w-full table-fixed border-collapse" aria-label="WAN interfaces">
                    <thead>
                      <tr>
                        <th scope="col" className={th}>
                          Interface
                        </th>
                        <th scope="col" className={th}>
                          Vendor
                        </th>
                        <th scope="col" className={th}>
                          Status
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {wans.map((w) => (
                        <tr key={`${w.vendor}-${w.interfaceName}`}>
                          <td className={td} style={mono}>
                            {w.interfaceName}
                          </td>
                          <td className={td}>{w.vendor}</td>
                          <td className={td}>
                            <StatusText status={w.vendorStatus} />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </Panel>
              <Panel title="Tunnel">
                {data.tunnel ? (
                  <div>
                    <dl>
                      {(
                        [
                          ['Tunnel', data.tunnel.tunnelStatus],
                          ['Device', data.tunnel.deviceStatus],
                          ['VPN mode', data.tunnel.vpnMode],
                        ] as const
                      ).map(([k, v]) => (
                        <div
                          key={k}
                          className="flex h-7 items-center gap-3 border-b border-[var(--color-border)] px-3 text-[12px]"
                        >
                          <dt className="w-28 shrink-0 text-[var(--color-muted)]">{k}</dt>
                          <dd style={mono}>{v ?? '-'}</dd>
                        </div>
                      ))}
                    </dl>
                    <ul className="text-[12px]">
                      {peers?.meraki.map((p) => (
                        <li
                          key={p.networkId}
                          className="flex h-7 items-center justify-between border-b border-[var(--color-border)] px-3"
                        >
                          <span className="truncate">{p.networkName}</span>
                          <StatusText status={p.reachability} />
                        </li>
                      ))}
                      {peers?.thirdParty.map((p) => (
                        <li
                          key={p.name}
                          className="flex h-7 items-center justify-between border-b border-[var(--color-border)] px-3"
                        >
                          <span className="truncate">
                            {p.name}{' '}
                            <span className="text-[var(--color-muted)]" style={mono}>
                              {p.publicIp}
                            </span>
                          </span>
                          <StatusText status={p.reachability} />
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : (
                  <Note>No tunnel data for this store.</Note>
                )}
              </Panel>
            </div>

            <Panel title="History">
              {history.deferred ? (
                <Note>Network history (poll buckets, VLAN clients, drift) is coming soon.</Note>
              ) : null}
            </Panel>
          </div>
        )}
      </div>
    </div>
  )
}
